import { Controller } from '@hotwired/stimulus'
import { createPageConfiguration } from '@app/application/export-configuration/behaviors/page-configuration'
import type { PageConfigurationType } from '@app/types/'

export default class extends Controller {
  static targets = [
    'paperSize',
    'topMargin',
    'bottomMargin',
    'supportedLocale',
    'multilingualHint',
  ]

  static values = {
    footerHeight: Number,
    landscapeFooterHeight: Number,
    headerHeight: Number,
    landscapeHeaderHeight: Number,
  }

  declare readonly paperSizeTarget: HTMLInputElement
  declare readonly topMarginTarget: HTMLInputElement
  declare readonly bottomMarginTarget: HTMLInputElement
  declare readonly supportedLocaleTargets: HTMLInputElement[]
  declare readonly multilingualHintTarget: HTMLElement
  declare readonly hasMultilingualHintTarget: boolean

  declare footerHeightValue: number
  declare landscapeFooterHeightValue: number
  declare headerHeightValue: number
  declare landscapeHeaderHeightValue: number

  private pageConfiguration!: PageConfigurationType

  connect(): void {
    this.pageConfiguration = createPageConfiguration()
    window.pageConfiguration = this.pageConfiguration

    this.pageConfiguration.footerHeight = this.footerHeightValue
    this.pageConfiguration.landscapeFooterHeight =
      this.landscapeFooterHeightValue
    this.pageConfiguration.headerHeight = this.headerHeightValue
    this.pageConfiguration.landscapeHeaderHeight =
      this.landscapeHeaderHeightValue

    this.pageConfiguration.setPageSize(this.paperSizeTarget.value)
    this.pageConfiguration.setTopMargin(this.topMarginTarget.valueAsNumber)
    this.pageConfiguration.setBottomMargin(
      this.bottomMarginTarget.valueAsNumber
    )

    this.pageConfiguration.doChecks()
  }

  supportedLocalesChanged(): void {
    if (!this.hasMultilingualHintTarget) {
      return
    }

    const enabledCount = this.supportedLocaleTargets.filter(
      (checkbox) => checkbox.checked
    ).length
    this.multilingualHintTarget.hidden = enabledCount <= 1
  }

  paperSizeChanged(): void {
    this.pageConfiguration.setPageSize(this.paperSizeTarget.value)
    this.pageConfiguration.doChecks()
  }

  topMarginChanged(): void {
    this.pageConfiguration.setTopMargin(parseFloat(this.topMarginTarget.value))
    this.pageConfiguration.doChecks()
  }

  bottomMarginChanged(): void {
    this.pageConfiguration.setBottomMargin(
      parseFloat(this.bottomMarginTarget.value)
    )
    this.pageConfiguration.doChecks()
  }

  imageChanged(event: Event): void {
    const input = event.target as HTMLInputElement
    const imageType = input.dataset.imageType

    if (!input.files || !input.files[0] || !imageType) {
      return
    }

    const image = new Image()
    image.onload = (): void => {
      const heightProperty = `${imageType}Height` as
        | 'footerHeight'
        | 'landscapeFooterHeight'
        | 'headerHeight'
        | 'landscapeHeaderHeight'
      this.pageConfiguration[heightProperty] = image.height
      this.pageConfiguration.doChecks()
    }
    image.src = URL.createObjectURL(input.files[0])
  }
}
